import moment from 'moment';

export const SLOT_UNAVAILABLE_MESSAGE =
    "We're sorry, but that time is no longer available.";

export const PENDING_HOLD_MINUTES = 5;

export function buildPendingHoldExpiry(nowMs: number = Date.now()) {
    const expiresAtDate = new Date(nowMs + PENDING_HOLD_MINUTES * 60 * 1000);
    return {
        expiresAtDate,
        expiration: moment(expiresAtDate).format('h:mm a'),
    };
}

export interface TeeTimeGroup {
    id?: string;
    date?: string;
    status?: string;
    expiration?: string;
    expiresAt?: unknown;
    golfers?: unknown[];
    startingTeeID?: string;
    startingTime?: string;
    reroundTeeID?: string;
    reroundTime?: string;
    numHoles?: number;
    blockTitle?: string;
    blockTeeID?: string;
    blockStartTime?: string;
    blockEndTime?: string;
    blockReroundTeeID?: string;
    blockReroundStartTime?: string;
    blockReroundEndTime?: string;
}

export interface TeeSlotCandidate {
    time: string;
    teeSheet: string;
    teeSheetName?: string;
    reroundTee?: string;
    roundLength?: number;
    minGroupSize?: number;
    requirePayment?: boolean;
}

export interface ReservationSlotInput {
    id?: string | null;
    startingTee?: string | null;
    time?: string | null;
    reroundTee?: string | null;
    roundLength?: number | null;
    numHoles?: number | null;
    numPlayers?: number | null;
    date?: string | null;
}

export function getSlotKey(teeSheetID: string, time: string): string {
    return `${teeSheetID}|${moment(time, 'h:mm a').format('h:mm a')}`;
}

/** Inclusive range check on wall-clock time (mirrors the widget's block filter). */
export function isTimeWithinBlockRange(
    time: string,
    startTime: string,
    endTime: string
): boolean {
    if (!time || !startTime || !endTime) return false;
    const t = moment(time, 'h:mm a').format('HH:mm');
    const s = moment(startTime, 'h:mm a').format('HH:mm');
    const e = moment(endTime, 'h:mm a').format('HH:mm');
    return t >= s && t <= e;
}

/**
 * True when a specific (teeSheet, time) slot falls inside any clubhouse block
 * for the given date. Block docs live alongside reservations in `teetimegroups`.
 * Title is intentionally NOT required (it is display-only); any block with a
 * start/end and matching tee counts.
 */
export function isSlotBlocked(
    groups: TeeTimeGroup[] | null | undefined,
    params: { teeSheetID?: string | null; time?: string | null; date?: string | null }
): boolean {
    const { teeSheetID, time, date } = params;
    if (!Array.isArray(groups) || !teeSheetID || !time) return false;
    const targetDate = moment(date).format('YYYY-MM-DD');

    return groups.some((group) => {
        if (!group) return false;
        if (moment(group?.date).format('YYYY-MM-DD') !== targetDate) return false;

        if (
            group.blockStartTime &&
            group.blockEndTime &&
            group.blockTeeID === teeSheetID &&
            isTimeWithinBlockRange(time, group.blockStartTime, group.blockEndTime)
        ) {
            return true;
        }

        if (
            group.blockReroundStartTime &&
            group.blockReroundEndTime &&
            group.blockReroundTeeID === teeSheetID &&
            isTimeWithinBlockRange(time, group.blockReroundStartTime, group.blockReroundEndTime)
        ) {
            return true;
        }

        return false;
    });
}

/** True when a reservation's starting slot (or 18-hole reround slot) is blocked. */
export function isReservationBlocked(
    groups: TeeTimeGroup[] | null | undefined,
    reservation: ReservationSlotInput
): boolean {
    if (!reservation) return false;
    const date = reservation.date;

    if (
        isSlotBlocked(groups, {
            teeSheetID: reservation.startingTee,
            time: reservation.time,
            date,
        })
    ) {
        return true;
    }

    if (
        reservation.numHoles === 18 &&
        reservation.reroundTee &&
        reservation.time &&
        reservation.roundLength != null
    ) {
        const reroundTime = moment(reservation.time, 'h:mm a')
            .add(reservation.roundLength, 'minutes')
            .format('h:mm a');
        if (
            isSlotBlocked(groups, {
                teeSheetID: reservation.reroundTee,
                time: reroundTime,
                date,
            })
        ) {
            return true;
        }
    }

    return false;
}

export function getGroupPlayerCount(group: TeeTimeGroup): number {
    const golfers = group?.golfers;
    if (Array.isArray(golfers)) return golfers.length;
    return 0;
}

function timestampToMs(value: unknown): number | null {
    if (value == null) return null;
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (value instanceof Date) return value.getTime();
    if (typeof value === 'object' && value !== null) {
        const withToDate = value as { toDate?: () => Date };
        if (typeof withToDate.toDate === 'function') {
            return withToDate.toDate().getTime();
        }
        const ts = value as { seconds?: number; nanoseconds?: number };
        if (typeof ts.seconds === 'number') {
            return ts.seconds * 1000 + (ts.nanoseconds || 0) / 1e6;
        }
    }
    return null;
}

function getExpiresAtMsFromLegacyExpiration(expiration: string): number {
    const parsed = moment(expiration, 'h:mm a', true);
    if (!parsed.isValid()) return NaN;

    const now = moment();
    const end = now
        .clone()
        .hour(parsed.hour())
        .minute(parsed.minute())
        .second(0)
        .millisecond(0);

    if (end.isBefore(now)) {
        end.add(1, 'day');
    }

    return end.valueOf();
}

/** Milliseconds when a pending hold expires, or NaN if unknown. */
export function getExpiresAtMs(group: TeeTimeGroup | null | undefined): number {
    if (!group) return NaN;

    const fromTimestamp = timestampToMs(group.expiresAt);
    if (fromTimestamp != null) return fromTimestamp;

    if (group.expiration) {
        return getExpiresAtMsFromLegacyExpiration(group.expiration);
    }

    return NaN;
}

export function isReservationExpired(
    group: TeeTimeGroup | null | undefined,
    nowMs: number = Date.now()
): boolean {
    if (!group || group.status !== 'pending') return false;
    const expiresAtMs = getExpiresAtMs(group);
    if (!Number.isFinite(expiresAtMs)) return true;
    return expiresAtMs <= nowMs;
}

export function getSecondsLeftUntilExpiry(
    expiresAt: unknown,
    expiration?: string | null,
    expireBufferSeconds = 0
): number {
    let expiresAtMs = timestampToMs(expiresAt);
    if (expiresAtMs == null && expiration) {
        expiresAtMs = getExpiresAtMsFromLegacyExpiration(expiration);
    }
    if (expiresAtMs == null || !Number.isFinite(expiresAtMs)) return NaN;

    const bufferMs = expireBufferSeconds * 1000;
    return Math.max(0, Math.floor((expiresAtMs - Date.now() - bufferMs) / 1000));
}

function hasExpiresAt(group: TeeTimeGroup | null | undefined): boolean {
    return timestampToMs(group?.expiresAt) != null;
}

/** Confirmed groups and pending groups that have not expired. */
export function isActiveTeeTimeGroup(group: TeeTimeGroup | null | undefined): boolean {
    if (!group) return false;
    if (group.status !== 'pending') return true;
    if (!hasExpiresAt(group)) return false;
    return !isReservationExpired(group);
}

export function buildSlotOccupancy(
    groups: TeeTimeGroup[],
    options: { excludeReservationId?: string | null; date: string }
): Record<string, number> {
    const occupancy: Record<string, number> = {};
    const targetDate = moment(options.date).format('YYYY-MM-DD');

    groups.forEach((group) => {
        if (!group) return;
        if (options.excludeReservationId && group.id === options.excludeReservationId) return;
        if (moment(group?.date).format('YYYY-MM-DD') !== targetDate) return;
        if (!isActiveTeeTimeGroup(group)) return;

        const players = getGroupPlayerCount(group);
        if (players <= 0) return;

        if (group.startingTeeID && group.startingTime) {
            const startKey = getSlotKey(group.startingTeeID, group.startingTime);
            occupancy[startKey] = (occupancy[startKey] || 0) + players;
        }
        if (group.numHoles === 18 && group.reroundTeeID && group.reroundTime) {
            const reroundKey = getSlotKey(group.reroundTeeID, group.reroundTime);
            occupancy[reroundKey] = (occupancy[reroundKey] || 0) + players;
        }
    });

    return occupancy;
}

export function getEffectiveMinGroupSize(
    reservation: { date?: string | null; time?: string | null; numHoles?: number | null },
    widgetRules?: Array<{
        active?: boolean;
        when?: { days?: string[] };
        startTime?: string;
        endTime?: string;
        minGroupSize18?: number;
        minGroupSize9?: number;
    }>
): number | null {
    const resDay = moment(reservation?.date).format('dddd');
    if (!Array.isArray(widgetRules) || !reservation?.time) return null;

    for (const rule of widgetRules) {
        if (!rule?.active) continue;
        if (!rule.when?.days?.includes(resDay)) continue;
        if (moment(reservation.time, 'h:mm a').isBefore(moment(rule.startTime, 'h:mm a'))) continue;
        if (moment(reservation.time, 'h:mm a').isAfter(moment(rule.endTime, 'h:mm a'))) continue;

        if (reservation?.numHoles === 18) return rule.minGroupSize18 ?? null;
        if (reservation?.numHoles === 9) return rule.minGroupSize9 ?? null;
        return null;
    }
    return null;
}

export function passesBaseCapacityFilter(params: {
    occupancy: Record<string, number>;
    slot: { teeSheet: string; time: string; reroundTee?: string; roundLength?: number };
    numPlayers: number;
    numHoles: number;
}): boolean {
    const { occupancy, slot, numPlayers, numHoles } = params;
    if (!numPlayers) return false;

    const startKey = getSlotKey(slot.teeSheet, slot.time);
    const startPlayers = occupancy[startKey] || 0;
    if (numPlayers > 4 - startPlayers) return false;

    if (numHoles === 18) {
        const reroundTime = moment(slot.time, 'h:mm a')
            .add(slot.roundLength, 'minutes')
            .format('h:mm a');
        const reroundKey = getSlotKey(slot.reroundTee || '', reroundTime);
        const reroundPlayers = occupancy[reroundKey] || 0;
        if (numPlayers > 4 - reroundPlayers) return false;
    }

    return true;
}

export function passesJoinOnlyFilter(params: {
    occupancy: Record<string, number>;
    slot: { teeSheet: string; time: string; reroundTee?: string; roundLength?: number; minGroupSize?: number };
    numPlayers: number;
    numHoles: number;
}): boolean {
    const { occupancy, slot, numPlayers, numHoles } = params;
    const startKey = getSlotKey(slot.teeSheet, slot.time);
    const startPlayers = occupancy[startKey] || 0;

    if (numHoles === 18) {
        const reroundTime = moment(slot.time, 'h:mm a')
            .add(slot.roundLength, 'minutes')
            .format('h:mm a');
        const reroundKey = getSlotKey(slot.reroundTee || '', reroundTime);
        const reroundPlayers = occupancy[reroundKey] || 0;

        if (numPlayers === 1) {
            return (
                startPlayers >= 1 &&
                startPlayers <= 3 &&
                reroundPlayers >= 1 &&
                reroundPlayers <= 3
            );
        }
        if (numPlayers === 2 && slot.minGroupSize === 3) {
            return (
                startPlayers >= 1 &&
                startPlayers <= 2 &&
                reroundPlayers >= 1 &&
                reroundPlayers <= 2
            );
        }
        return true;
    }

    if (numPlayers === 1) {
        return startPlayers >= 1 && startPlayers <= 3;
    }
    if (numPlayers === 2 && slot.minGroupSize === 3) {
        return startPlayers >= 1 && startPlayers <= 2;
    }
    return true;
}

export function canBookSlot(params: {
    occupancy: Record<string, number>;
    reservation: ReservationSlotInput;
    widgetRules?: Array<{
        active?: boolean;
        when?: { days?: string[] };
        startTime?: string;
        endTime?: string;
        minGroupSize18?: number;
        minGroupSize9?: number;
    }>;
    effectiveMinGroupSize?: number | null;
    groups?: TeeTimeGroup[];
}): { ok: boolean; reason?: 'capacity' | 'join_only' | 'blocked' } {
    const { occupancy, reservation, widgetRules, groups } = params;
    const requestedPlayers = Number(reservation?.numPlayers) || 0;
    if (!requestedPlayers || !reservation?.time || !reservation?.startingTee) {
        return { ok: false, reason: 'capacity' };
    }

    if (Array.isArray(groups) && isReservationBlocked(groups, reservation)) {
        return { ok: false, reason: 'blocked' };
    }

    const slot = {
        teeSheet: reservation.startingTee,
        time: reservation.time,
        reroundTee: reservation.reroundTee || undefined,
        roundLength: reservation.roundLength || undefined,
        minGroupSize:
            params.effectiveMinGroupSize ??
            getEffectiveMinGroupSize(reservation, widgetRules) ??
            undefined,
    };

    if (
        !passesBaseCapacityFilter({
            occupancy,
            slot,
            numPlayers: requestedPlayers,
            numHoles: Number(reservation.numHoles) || 0,
        })
    ) {
        return { ok: false, reason: 'capacity' };
    }

    if (
        !passesJoinOnlyFilter({
            occupancy,
            slot,
            numPlayers: requestedPlayers,
            numHoles: Number(reservation.numHoles) || 0,
        })
    ) {
        return { ok: false, reason: 'join_only' };
    }

    return { ok: true };
}

export type SlotEvaluation = {
    available: boolean;
    reasons: string[];
    occupancyOnStart: number;
    occupancyOnReround?: number;
};

export type WidgetRule = {
    active?: boolean;
    name?: string;
    when?: { days?: string[] };
    startTime?: string;
    endTime?: string;
    minGroupSize18?: number;
    minGroupSize9?: number;
    allow18Holes?: string[];
    allow9Holes?: string[];
    requirePayment?: string;
};

function getStartAndReroundOccupancy(
    occupancy: Record<string, number>,
    slot: { teeSheet: string; time: string; reroundTee?: string; roundLength?: number },
    numHoles: number
): { startPlayers: number; reroundPlayers: number } {
    const startKey = getSlotKey(slot.teeSheet, slot.time);
    const startPlayers = occupancy[startKey] || 0;
    let reroundPlayers = 0;
    if (numHoles === 18 && slot.reroundTee && slot.roundLength != null) {
        const reroundTime = moment(slot.time, 'h:mm a')
            .add(slot.roundLength, 'minutes')
            .format('h:mm a');
        reroundPlayers = occupancy[getSlotKey(slot.reroundTee, reroundTime)] || 0;
    }
    return { startPlayers, reroundPlayers };
}

export function getBaseCapacityDenialReasons(params: {
    occupancy: Record<string, number>;
    slot: { teeSheet: string; time: string; reroundTee?: string; roundLength?: number };
    numPlayers: number;
    numHoles: number;
}): string[] {
    const { occupancy, slot, numPlayers, numHoles } = params;
    const reasons: string[] = [];
    if (!numPlayers) {
        reasons.push('No players selected');
        return reasons;
    }

    const { startPlayers, reroundPlayers } = getStartAndReroundOccupancy(occupancy, slot, numHoles);

    if (numPlayers > 4 - startPlayers) {
        reasons.push(
            `Slot full on start tee (${startPlayers}/4 booked; adding ${numPlayers} would exceed 4)`
        );
    }

    if (numHoles === 18 && numPlayers > 4 - reroundPlayers) {
        reasons.push(
            `Slot full on reround tee (${reroundPlayers}/4 booked; adding ${numPlayers} would exceed 4)`
        );
    }

    return reasons;
}

export function getJoinOnlyDenialReasons(params: {
    occupancy: Record<string, number>;
    slot: { teeSheet: string; time: string; reroundTee?: string; roundLength?: number; minGroupSize?: number };
    numPlayers: number;
    numHoles: number;
}): string[] {
    const { occupancy, slot, numPlayers, numHoles } = params;
    if (passesJoinOnlyFilter({ occupancy, slot, numPlayers, numHoles })) {
        return [];
    }

    const { startPlayers, reroundPlayers } = getStartAndReroundOccupancy(occupancy, slot, numHoles);
    const reasons: string[] = [];

    if (numHoles === 18) {
        if (numPlayers === 1) {
            reasons.push(
                `Single must join existing group on start (1–3 players); reround (1–3 players). Currently start: ${startPlayers}, reround: ${reroundPlayers}`
            );
        } else if (numPlayers === 2 && slot.minGroupSize === 3) {
            reasons.push(
                `Twosome (min group 3) must join existing group on start (1–2) and reround (1–2). Currently start: ${startPlayers}, reround: ${reroundPlayers}`
            );
        }
        return reasons;
    }

    if (numPlayers === 1) {
        reasons.push(
            `Single must join existing group (1–3 players on slot); currently ${startPlayers} on slot`
        );
    } else if (numPlayers === 2 && slot.minGroupSize === 3) {
        reasons.push(
            `Twosome (min group 3) must join slot with 1–2 players; currently ${startPlayers} on slot`
        );
    }

    return reasons;
}

/** Non-mutating widget rule checks (mirrors filterWidgetRules in AvailableTimes). */
export function getWidgetRuleDenialReasons(params: {
    slot: TeeSlotCandidate;
    reservation: { date?: string | null; numHoles?: number | null; numPlayers?: number | null };
    widgetRules?: WidgetRule[];
    allow18Holes: string[];
    allow9Holes: string[];
}): { reasons: string[]; minGroupSize?: number } {
    const { slot, reservation, widgetRules, allow18Holes, allow9Holes } = params;
    const reasons: string[] = [];
    const resDay = moment(reservation.date).format('dddd');
    let useDefaultSettings = true;
    let minGroupSize: number | undefined;

    widgetRules?.forEach((rule) => {
        if (!rule?.active || !rule.when?.days?.includes(resDay)) return;
        if (moment(slot.time, 'h:mm a').isBefore(moment(rule.startTime, 'h:mm a'))) return;
        if (moment(slot.time, 'h:mm a').isAfter(moment(rule.endTime, 'h:mm a'))) return;

        useDefaultSettings = false;
        if (reservation.numHoles === 18) {
            minGroupSize = rule.minGroupSize18;
        } else if (reservation.numHoles === 9) {
            minGroupSize = rule.minGroupSize9;
        }

        const ruleLabel = rule.name ? `rule "${rule.name}"` : 'active rule';

        if (reservation.numHoles === 18 && !rule.allow18Holes?.includes(slot.teeSheet)) {
            reasons.push(`18 holes not allowed on ${slot.teeSheetName || slot.teeSheet} (${ruleLabel})`);
        }
        if (reservation.numHoles === 9 && !rule.allow9Holes?.includes(slot.teeSheet)) {
            reasons.push(`9 holes not allowed on ${slot.teeSheetName || slot.teeSheet} (${ruleLabel})`);
        }

        const isSingle = reservation.numPlayers === 1;
        const isTwosomeMin3Exception =
            reservation.numPlayers === 2 &&
            ((reservation.numHoles === 18 && rule.minGroupSize18 === 3) ||
                (reservation.numHoles === 9 && rule.minGroupSize9 === 3));

        if (!isSingle && !isTwosomeMin3Exception) {
            if (reservation.numHoles === 18 && reservation.numPlayers! < (rule.minGroupSize18 ?? 0)) {
                reasons.push(
                    `Minimum ${rule.minGroupSize18} players required for 18 holes (${ruleLabel})`
                );
            } else if (reservation.numHoles === 9 && reservation.numPlayers! < (rule.minGroupSize9 ?? 0)) {
                reasons.push(
                    `Minimum ${rule.minGroupSize9} players required for 9 holes (${ruleLabel})`
                );
            }
        }
    });

    if (reservation.numHoles === 18 && useDefaultSettings && !allow18Holes.includes(slot.teeSheet)) {
        reasons.push(`18 holes not allowed on ${slot.teeSheetName || slot.teeSheet} (default settings)`);
    }
    if (reservation.numHoles === 9 && useDefaultSettings && !allow9Holes.includes(slot.teeSheet)) {
        reasons.push(`9 holes not allowed on ${slot.teeSheetName || slot.teeSheet} (default settings)`);
    }

    return { reasons, minGroupSize };
}

export function evaluateSlotAvailability(params: {
    slot: TeeSlotCandidate;
    reservation: { date?: string | null; numHoles?: number | null; numPlayers?: number | null };
    occupancy: Record<string, number>;
    widgetRules?: WidgetRule[];
    allow18Holes: string[];
    allow9Holes: string[];
    timeSlotStillOnSheet: boolean;
    blockedReason?: string;
    failsAdvanceWindow: boolean;
    fails18Reround: boolean;
    advanceMinutes?: number;
    reroundTimeLabel?: string;
}): SlotEvaluation {
    const {
        slot,
        reservation,
        occupancy,
        widgetRules,
        allow18Holes,
        allow9Holes,
        timeSlotStillOnSheet,
        blockedReason,
        failsAdvanceWindow,
        fails18Reround,
        advanceMinutes,
        reroundTimeLabel,
    } = params;

    const numPlayers = Number(reservation.numPlayers) || 0;
    const numHoles = Number(reservation.numHoles) || 0;
    const { startPlayers, reroundPlayers } = getStartAndReroundOccupancy(occupancy, slot, numHoles);

    const reasons: string[] = [];

    if (blockedReason) {
        reasons.push(blockedReason);
    } else if (!timeSlotStillOnSheet) {
        reasons.push('Removed from tee sheet schedule (blocked)');
    }

    if (failsAdvanceWindow) {
        const mins = advanceMinutes ?? 0;
        reasons.push(
            `Too soon for today (requires ${mins} min in advance; now ${moment().format('h:mm a')})`
        );
    }

    if (fails18Reround) {
        reasons.push(
            `Reround time ${reroundTimeLabel || 'unknown'} not on reround tee sheet schedule`
        );
    }

    if (!numHoles || !numPlayers) {
        reasons.push('Select number of holes and players to evaluate');
        return {
            available: false,
            reasons,
            occupancyOnStart: startPlayers,
            occupancyOnReround: numHoles === 18 ? reroundPlayers : undefined,
        };
    }

    reasons.push(...getBaseCapacityDenialReasons({ occupancy, slot, numPlayers, numHoles }));

    const widgetResult = getWidgetRuleDenialReasons({
        slot,
        reservation,
        widgetRules,
        allow18Holes,
        allow9Holes,
    });
    reasons.push(...widgetResult.reasons);

    const slotWithMinGroup = { ...slot, minGroupSize: widgetResult.minGroupSize ?? slot.minGroupSize };
    reasons.push(
        ...getJoinOnlyDenialReasons({
            occupancy,
            slot: slotWithMinGroup,
            numPlayers,
            numHoles,
        })
    );

    return {
        available: reasons.length === 0,
        reasons,
        occupancyOnStart: startPlayers,
        occupancyOnReround: numHoles === 18 ? reroundPlayers : undefined,
    };
}
